import { AbstractControl } from '@angular/forms';
import { combineLatest, filter, mapTo, Observable, of, skip, startWith, tap } from 'rxjs';
import {
  calculateAskingRentYearlyTotal,
  divide,
  divideBy12,
  multiply,
  multiplyBy12,
  RoundFn,
} from '@marketiq/calc-core';
import { LocaleContext } from '../ports/auto-calc-ports';

/**
 * Availability asking-rent matrix orchestration, moved verbatim from the host
 * `availability-auto-calc-listener-helper.ts`. Three substitutions vs the host original, each
 * byte-equivalent (see the regression doc + adversarial verification):
 *   1. `formatNumber(...,'en',col.format||'0.2-2') + parseFormattedNumber(...)` -> the injected
 *      {@link RoundFn} (`autoCalcRound`). All written rate columns have maxFraction 2 (`.2-2`/`.0-2`/
 *      NULL), so the parsed numeric result is identical; the host already parses via
 *      `parseFormattedNumber`, so Poland is already correct here (no delta).
 *   2. `new UserLocaleService().isAusUser()/isPacificUserFromSSO()` -> {@link LocaleContext} flags
 *      captured at attach time (SSO-session-stable, so attach-time == fire-time).
 *   3. host `Column` + `AskingRent` enum -> the structural {@link RatesColumnLike} (only `.field` is
 *      read; `.options.format` is no longer needed) + the local `AVAIL_FIELD` field-name constants.
 *
 * The arithmetic (`multiplyBy12`/`divideBy12`/`multiply`/`divide`/`calculateAskingRentYearlyTotal`) is
 * single-sourced from `@marketiq/calc-core`.
 */

/** Asking-rent field names (the host `AskingRent` enum values) the matrix switches on. */
const AVAIL_FIELD = {
  AskingRent: 'asking_rental_rate_yearly',
  AskingRentYearlyTotal: 'asking_rental_amount_yearly',
  SpaceSize: 'available_space',
  AskingRentalRateMonthly: 'asking_rental_rate_monthly',
  TotalRentEurosPerMonth: 'asking_rental_amount_monthly',
} as const;

/**
 * Service-charge field names the listener switches on.
 *
 * - `ServiceCharge`       — per-area service charge rate (`cam_charges_per_area_value`)
 * - `ServiceChargePerMonth` — total monthly service charge (`cam_charges`)
 * - `ServiceChargePerYear`  — total yearly service charge (`service_charge_amount_yearly`)
 */
const SERVICE_CHARGE_FIELD = {
  ServiceCharge: 'cam_charges_per_area_value',
  ServiceChargePerMonth: 'cam_charges',
  ServiceChargePerYear: 'service_charge_amount_yearly',
} as const;

/** `ref_rate_type_desc` value that selects the AUS-Net (opex-included) branch. */
const ASKING_RATE_TYPE_NET = 'Net';

/** Catalog column as the matrix reads it (neutral subset — only `field` is used). */
export interface RatesColumnLike {
  field?: string;
  options?: { format?: string };
}

const multiplyValues = (value1: number, value2: number): number => multiply(value1, value2);
const divideValues = (value1: number, value2: number): number => divide(value1, value2);

/**
 * Host-controlled behavior switches for {@link createAvailabilityRatesBinders}.
 *
 * `enableChainedSizeFallbacks` (default false — legacy behavior): when true,
 * a size change also recomputes rents/service charges for records captured
 * with only a yearly rate or only totals (deriving the missing driver value).
 * The host enables this for MIQ 2.0 only, so MIQ 1.0 keeps the historical
 * matrix byte-for-byte.
 */
export interface AvailabilityRatesBinderOptions {
  enableChainedSizeFallbacks?: boolean;
}

/** (askingRent + opex) x spaceSize — the AUS-Net / Pacific yearly total (isPacific=true branch). */
const calculateThreeValues = (askingRent: number, opex: number, spaceSize: number): number =>
  calculateAskingRentYearlyTotal(askingRent, opex, spaceSize, true);

type MultipleRatesControlsListener = (
  askingRentCtrl: AbstractControl,
  spaceSizeCtrl: AbstractControl,
  opexCtrl: AbstractControl,
  spaceSizeUOMCtrl: AbstractControl,
  askingRateTypeCtrl: AbstractControl,
  askingRentYearlyColumn: RatesColumnLike,
  askingRentYearlyTotalCtrl: AbstractControl,
  shouldSkip?: boolean
) => Observable<unknown> | undefined;

type SingleRatesControlsListener = (
  listeenerColumn: RatesColumnLike,
  ctrlOne: AbstractControl,
  ctrlTwo: AbstractControl,
  affectedColumn: RatesColumnLike,
  affectedFormCtrl: AbstractControl,
  affectedColumnTwo?: RatesColumnLike,
  affectedFormCtrlTwo?: AbstractControl,
  affectedColumnThree?: RatesColumnLike,
  affectedFormCtrlThree?: AbstractControl,
  shouldSkip?: boolean
) => Observable<unknown> | undefined;

/** Round + write + mark dirty/touched. Mirrors the host `updateValue` (guards preserved). */
function updateValue(result: number, round: RoundFn, affectedFormCtrl: AbstractControl | null | undefined): void {
  if (result != null && !isNaN(result) && isFinite(result) && affectedFormCtrl) {
    const finalResult = round(result);
    if (!isNaN(finalResult) && isFinite(finalResult)) {
      affectedFormCtrl.patchValue(finalResult, { emitEvent: false });
      affectedFormCtrl.markAsDirty();
      affectedFormCtrl.markAsTouched();
    }
  }
}

/** Round + write + mark dirty/touched. Mirrors the host `updateAskingRentYealyValue` (no guard). */
function updateAskingRentYearlyValue(
  askingRentYearlyTotalCtrl: AbstractControl | null | undefined,
  value: number,
  round: RoundFn
): void {
  const result = round(value);
  if (askingRentYearlyTotalCtrl) {
    askingRentYearlyTotalCtrl.patchValue(result, { emitEvent: false });
    askingRentYearlyTotalCtrl.markAsDirty();
    askingRentYearlyTotalCtrl.markAsTouched();
  }
}

function multipleListenerToRatesFactory(
  valueModifier: (askingRent: number, opex: number, spaceSize: number) => number,
  round: RoundFn,
  locale: LocaleContext
): MultipleRatesControlsListener {
  return function severalListenerFn(
    askingRentCtrl: AbstractControl,
    spaceSizeCtrl: AbstractControl,
    opexCtrl: AbstractControl,
    spaceSizeUOMCtrl: AbstractControl,
    askingRateTypeCtrl: AbstractControl,
    askingRentYearlyColumn: RatesColumnLike,
    askingRentYearlyTotalCtrl: AbstractControl,
    shouldSkip = false
  ): Observable<unknown> | undefined {
    if (askingRentCtrl != null && spaceSizeCtrl != null && spaceSizeUOMCtrl != null) {
      const opexSource$ = opexCtrl
        ? opexCtrl.valueChanges.pipe(startWith(opexCtrl.value), mapTo(opexCtrl))
        : of(null as AbstractControl | null);

      let listener$ = combineLatest([
        askingRentCtrl.valueChanges.pipe(startWith(askingRentCtrl.value), mapTo(askingRentCtrl)),
        spaceSizeCtrl.valueChanges.pipe(startWith(spaceSizeCtrl.value), mapTo(spaceSizeCtrl)),
        opexSource$,
        spaceSizeUOMCtrl.valueChanges.pipe(startWith(spaceSizeUOMCtrl.value), mapTo(spaceSizeUOMCtrl)),
        askingRateTypeCtrl.valueChanges.pipe(startWith(askingRateTypeCtrl.value), mapTo(askingRateTypeCtrl)),
      ]);

      // skip initialization emitted value for combineLatest
      if (shouldSkip) {
        listener$ = listener$.pipe(skip(1));
      }

      return listener$.pipe(
        tap(([askingRent, spaceSize, opex, spaceSizeUOM, askingRateType]) => {
          // When opexCtrl is absent (e.g. NZ), treat opex as 0 so the formula
          // reduces to the correct plain askingRent × spaceSize.
          const opexValue: number = opex != null ? opex.value ?? 0 : 0;
          if (askingRent.value != null && spaceSize.value != null) {
            // Product spec confirmed 2026-08-21:
            //   AU + Net   -> (Asking Rent $psqm + OPEX $psqm) x Space Size
            //   AU + Gross -> Space Size x Asking Rent
            //   NZ and every other market -> Space Size x Asking Rent
            // Outgoings are therefore an Australian-Net concern and nothing else. The previous
            // `locale.isPacific && !locale.isNZ` branch that used to sit here also caught Australian
            // GROSS — Pacific being AUS u NZ, it matched every Australian record and made Asking Rate
            // Type irrelevant, inflating Gross totals by the outgoings. Bulk edit's payload path
            // already gated on AUS+Net only, so removing it aligns the two surfaces.
            //
            // The AUS-Net test deliberately does NOT require a space-size UOM. It used to, and such
            // rows fell through to the Pacific branch which applied the same formula anyway. With
            // that branch gone, keeping the check would silently drop the outgoings from an
            // Australian Net record whose space-size UOM happens to be blank.
            if (locale.isAus && askingRateType.value === ASKING_RATE_TYPE_NET) {
              const modifiedValue = valueModifier(askingRent.value, opexValue, spaceSize.value);
              updateAskingRentYearlyValue(askingRentYearlyTotalCtrl, modifiedValue, round);
            } else {
              const result = calculateAskingRentYearlyTotal(askingRent.value, opexValue, spaceSize.value, false);
              updateAskingRentYearlyValue(askingRentYearlyTotalCtrl, result, round);
            }
          }
        })
      );
    }
    return undefined;
  };
}

function singleListenerToRatesFactory(
  round: RoundFn,
  locale: LocaleContext,
  enableChainedSizeFallbacks = false
): SingleRatesControlsListener {
  return function multipleListenerFn(
    listeenerColumn: RatesColumnLike,
    ctrlOne: AbstractControl,
    ctrlTwo: AbstractControl,
    affectedColumn: RatesColumnLike,
    affectedFormCtrl: AbstractControl,
    affectedColumnTwo?: RatesColumnLike,
    affectedFormCtrlTwo?: AbstractControl,
    affectedColumnThree?: RatesColumnLike,
    affectedFormCtrlThree?: AbstractControl,
    shouldSkip?: boolean
  ): Observable<unknown> | undefined {
    if (ctrlOne && ctrlTwo) {
      let listener$ = ctrlOne?.valueChanges.pipe(
        startWith(ctrlOne?.value),
        filter(() => ctrlOne?.value !== ''),
        mapTo(ctrlOne)
      );
      if (shouldSkip) {
        listener$ = listener$.pipe(skip(1));
      }
      return listener$.pipe(
        tap((ctrlOne: AbstractControl) => {
          let askingRentalRateMonthlyValue = 0;
          let askingRentYearlyTotalValue = 0;
          let askingRentValue = 0;
          let totalRentEurosPerMonthValue = 0;
          const field = listeenerColumn?.field;
          const hasCtrlOneValue = ctrlOne?.value != null;
          if (!hasCtrlOneValue) {
            // Rate field cleared — propagate null to derived fields
            const clearablePairs: Record<
              string,
              Array<[RatesColumnLike | undefined, AbstractControl | null | undefined]>
            > = {
              [AVAIL_FIELD.AskingRent]: [
                [affectedColumn, affectedFormCtrl],
                [affectedColumnTwo, affectedFormCtrlTwo],
                [affectedColumnThree, affectedFormCtrlThree],
              ],
            };
            (clearablePairs[field ?? ''] ?? []).forEach(([, ctrl]) => {
              ctrl?.patchValue(null, { emitEvent: false });
            });
          } else {
            switch (field) {
              case AVAIL_FIELD.SpaceSize: {
                const spaceSize = ctrlOne?.value;
                let monthlyRate = ctrlTwo?.value;
                // Chained-calculation fallback: the matrix's only size-driven
                // input edge was the monthly rate, so records captured with a
                // yearly rate never recomputed on a (possibly programmatic)
                // size change. Derive monthly = yearly/12 — the direction the
                // AskingRent branch already uses — and back-fill it silently
                // so no other listener fires. Pacific/AUS excluded: their
                // yearly total is opex-INCLUSIVE (multipleListenerToRatesFactory)
                // and a derived opex-exclusive monthly total would contradict it.
                let monthlyRateDerivedFromYearly = false;
                const yearlyRate = affectedFormCtrlThree?.value;
                if (
                  enableChainedSizeFallbacks &&
                  !locale.isPacific &&
                  !locale.isAus &&
                  (monthlyRate == null || isNaN(monthlyRate)) &&
                  yearlyRate != null &&
                  !isNaN(yearlyRate)
                ) {
                  monthlyRate = divideBy12(yearlyRate);
                  updateValue(monthlyRate, round, ctrlTwo);
                  monthlyRateDerivedFromYearly = true;
                }

                if (
                  spaceSize != null &&
                  !isNaN(spaceSize) &&
                  spaceSize > 0 &&
                  monthlyRate != null &&
                  !isNaN(monthlyRate)
                ) {
                  if (affectedColumn?.field === AVAIL_FIELD.TotalRentEurosPerMonth) {
                    totalRentEurosPerMonthValue = multiplyValues(spaceSize, monthlyRate);
                    updateValue(totalRentEurosPerMonthValue, round, affectedFormCtrl);
                  }
                  if (affectedColumnTwo?.field === AVAIL_FIELD.AskingRentYearlyTotal) {
                    const totalMonthly = multiplyValues(spaceSize, monthlyRate);
                    askingRentYearlyTotalValue = multiplyBy12(totalMonthly);
                    updateValue(askingRentYearlyTotalValue, round, affectedFormCtrlTwo);
                  }
                  // when the monthly rate was derived FROM the yearly rate,
                  // do not write the yearly rate back (rounding drift on the
                  // user's source value)
                  if (!monthlyRateDerivedFromYearly && affectedColumnThree?.field === AVAIL_FIELD.AskingRent) {
                    askingRentValue = multiplyBy12(monthlyRate);
                    updateValue(askingRentValue, round, affectedFormCtrlThree);
                  }
                }
                break;
              }

              case AVAIL_FIELD.TotalRentEurosPerMonth:
                if (
                  affectedColumn?.field === AVAIL_FIELD.AskingRentalRateMonthly &&
                  ctrlTwo?.value &&
                  ctrlTwo.value !== 0
                ) {
                  askingRentalRateMonthlyValue = divideValues(ctrlOne?.value, ctrlTwo?.value);
                  updateValue(askingRentalRateMonthlyValue, round, affectedFormCtrl);
                }
                if (affectedColumnTwo?.field === AVAIL_FIELD.AskingRentYearlyTotal) {
                  askingRentYearlyTotalValue = multiplyBy12(ctrlOne?.value);
                  updateValue(askingRentYearlyTotalValue, round, affectedFormCtrlTwo);
                }
                if (affectedColumnThree?.field === AVAIL_FIELD.AskingRent && ctrlTwo?.value && ctrlTwo.value !== 0) {
                  const monthlyRate = divideValues(ctrlOne?.value, ctrlTwo?.value);
                  askingRentValue = multiplyBy12(monthlyRate);
                  updateValue(askingRentValue, round, affectedFormCtrlThree);
                }
                break;

              case AVAIL_FIELD.AskingRentalRateMonthly:
                if (affectedColumn?.field === AVAIL_FIELD.TotalRentEurosPerMonth && ctrlTwo?.value) {
                  totalRentEurosPerMonthValue = multiplyValues(ctrlOne?.value, ctrlTwo?.value);
                  updateValue(totalRentEurosPerMonthValue, round, affectedFormCtrl);
                }
                if (affectedColumnTwo?.field === AVAIL_FIELD.AskingRentYearlyTotal) {
                  askingRentYearlyTotalValue = multiplyBy12(totalRentEurosPerMonthValue);
                  updateValue(askingRentYearlyTotalValue, round, affectedFormCtrlTwo);
                }
                if (affectedColumnThree?.field === AVAIL_FIELD.AskingRent) {
                  askingRentValue = multiplyBy12(ctrlOne?.value);
                  updateValue(askingRentValue, round, affectedFormCtrlThree);
                }
                break;

              case AVAIL_FIELD.AskingRentYearlyTotal:
                if (affectedColumnTwo?.field === AVAIL_FIELD.TotalRentEurosPerMonth) {
                  totalRentEurosPerMonthValue = divideBy12(ctrlOne?.value);
                  updateValue(totalRentEurosPerMonthValue, round, affectedFormCtrlTwo);
                }
                if (
                  affectedColumn?.field === AVAIL_FIELD.AskingRentalRateMonthly &&
                  ctrlTwo?.value &&
                  ctrlTwo.value !== 0
                ) {
                  askingRentalRateMonthlyValue = divideValues(totalRentEurosPerMonthValue, ctrlTwo?.value);
                  updateValue(askingRentalRateMonthlyValue, round, affectedFormCtrl);
                }
                // For Non Australian users, if the yearly total is updated, we want to update the asking rent (monthly) based on the new yearly total / space size
                if (
                  affectedColumnThree?.field === AVAIL_FIELD.AskingRent &&
                  ctrlTwo?.value &&
                  ctrlTwo.value !== 0 &&
                  !locale.isAus
                ) {
                  const monthlyRate = divideValues(totalRentEurosPerMonthValue, ctrlTwo?.value);
                  askingRentValue = multiplyBy12(monthlyRate);
                  updateValue(askingRentValue, round, affectedFormCtrlThree);
                }
                break;

              case AVAIL_FIELD.AskingRent:
                if (affectedColumn?.field === AVAIL_FIELD.AskingRentYearlyTotal && ctrlTwo?.value) {
                  askingRentYearlyTotalValue = multiplyValues(ctrlOne?.value, ctrlTwo?.value);
                  updateValue(askingRentYearlyTotalValue, round, affectedFormCtrl);
                }
                if (affectedColumnThree?.field === AVAIL_FIELD.TotalRentEurosPerMonth) {
                  totalRentEurosPerMonthValue = divideBy12(askingRentYearlyTotalValue);
                  updateValue(totalRentEurosPerMonthValue, round, affectedFormCtrlThree);
                }
                if (affectedColumnTwo?.field === AVAIL_FIELD.AskingRentalRateMonthly) {
                  askingRentalRateMonthlyValue = divideBy12(ctrlOne?.value);
                  updateValue(askingRentalRateMonthlyValue, round, affectedFormCtrlTwo);
                }
                break;
              default:
                break;
            }
          }
        })
      );
    }
    return undefined;
  };
}

/**
 * Asking Rent $psqm from Yearly Total / Space Size (NZ users). Mirrors the host
 * `addListenerToAskingRentPsqm`; the NZ locale gate stays at the caller.
 */
function askingRentPsqmListener(
  askingRentYearlyTotalCtrl: AbstractControl,
  spaceSizeCtrl: AbstractControl,
  askingRentCtrl: AbstractControl,
  round: RoundFn,
  shouldSkip = false
): Observable<unknown> | undefined {
  if (!askingRentYearlyTotalCtrl || !spaceSizeCtrl || !askingRentCtrl) {
    return undefined;
  }

  let listener$ = askingRentYearlyTotalCtrl.valueChanges;
  if (shouldSkip) {
    listener$ = listener$.pipe(skip(1));
  }
  return listener$.pipe(
    tap((yearlyTotal: unknown) => {
      const spaceSize: unknown = spaceSizeCtrl.value;
      if (yearlyTotal != null && spaceSize != null && (spaceSize as number) !== 0) {
        const askingRentPsqm = (yearlyTotal as number) / (spaceSize as number);
        updateValue(askingRentPsqm, round, askingRentCtrl);
      }
    })
  );
}

/**
 * Service-charge auto-calculation matrix. Mirrors {@link singleListenerToRatesFactory} but
 * switches on {@link SERVICE_CHARGE_FIELD} values and applies service-charge arithmetic:
 * - SpaceSize × ServiceCharge → ServiceChargePerMonth; × 12 → ServiceChargePerYear
 * - ServiceCharge × SpaceSize → ServiceChargePerMonth; × 12 → ServiceChargePerYear
 * - ServiceChargePerMonth ÷ SpaceSize → ServiceCharge; × 12 → ServiceChargePerYear
 * - ServiceChargePerYear ÷ 12 → ServiceChargePerMonth; ÷ SpaceSize → ServiceCharge
 */
function serviceChargeListener(
  round: RoundFn,
  locale: LocaleContext,
  enableChainedSizeFallbacks = false
): SingleRatesControlsListener {
  return function serviceChargeListenerFn(
    listeenerColumn: RatesColumnLike,
    ctrlOne: AbstractControl,
    ctrlTwo: AbstractControl,
    affectedColumn: RatesColumnLike,
    affectedFormCtrl: AbstractControl,
    affectedColumnTwo?: RatesColumnLike,
    affectedFormCtrlTwo?: AbstractControl,
    affectedColumnThree?: RatesColumnLike,
    affectedFormCtrlThree?: AbstractControl,
    shouldSkip?: boolean
  ): Observable<unknown> | undefined {
    if (ctrlOne && ctrlTwo) {
      let listener$ = ctrlOne.valueChanges.pipe(
        startWith(ctrlOne.value),
        filter(() => ctrlOne.value !== ''),
        mapTo(ctrlOne)
      );
      if (shouldSkip) {
        listener$ = listener$.pipe(skip(1));
      }
      return listener$.pipe(
        tap((ctrlOne: AbstractControl) => {
          let serviceChargeVal = 0;
          let serviceChargeMonthly = 0;
          let serviceChargeYearly = 0;
          const field = listeenerColumn?.field;
          if (ctrlOne.value != null) {
            switch (field) {
              case AVAIL_FIELD.SpaceSize: {
                const spaceSize: number = ctrlOne.value;
                const serviceCharge: number = ctrlTwo.value;
                if (spaceSize == null || isNaN(spaceSize) || spaceSize <= 0) {
                  break;
                }
                if (serviceCharge != null && !isNaN(serviceCharge)) {
                  // Compute monthly once; reuse for yearly to avoid redundant multiplication.
                  serviceChargeMonthly = multiplyValues(spaceSize, serviceCharge);
                  if (affectedColumn?.field === SERVICE_CHARGE_FIELD.ServiceChargePerMonth) {
                    updateValue(serviceChargeMonthly, round, affectedFormCtrl);
                  }
                  if (affectedColumnTwo?.field === SERVICE_CHARGE_FIELD.ServiceChargePerYear) {
                    serviceChargeYearly = multiplyBy12(serviceChargeMonthly);
                    updateValue(serviceChargeYearly, round, affectedFormCtrlTwo);
                  }
                  break;
                }
                if (!enableChainedSizeFallbacks) {
                  break; // MIQ 1.0: legacy matrix — per-area-driven records only
                }
                // Chained-calculation fallback: totals-driven records (per-area
                // rate empty) never recomputed on a size change. The totals are
                // user-entered facts and stay authoritative; re-derive the
                // per-area rate from them — the same arithmetic the
                // ServiceChargePerMonth / ServiceChargePerYear branches apply.
                const monthlyTotal =
                  affectedColumn?.field === SERVICE_CHARGE_FIELD.ServiceChargePerMonth ? affectedFormCtrl?.value : null;
                const yearlyTotal =
                  affectedColumnTwo?.field === SERVICE_CHARGE_FIELD.ServiceChargePerYear
                    ? affectedFormCtrlTwo?.value
                    : null;
                if (monthlyTotal != null && !isNaN(monthlyTotal)) {
                  updateValue(divideValues(monthlyTotal, spaceSize), round, ctrlTwo);
                  // derive the yearly total only when it is empty — a
                  // populated yearly total is a user-entered fact (e.g. a
                  // negotiated annual charge) and must not be overwritten
                  if (
                    affectedColumnTwo?.field === SERVICE_CHARGE_FIELD.ServiceChargePerYear &&
                    (yearlyTotal == null || isNaN(yearlyTotal))
                  ) {
                    updateValue(multiplyBy12(monthlyTotal), round, affectedFormCtrlTwo);
                  }
                } else if (yearlyTotal != null && !isNaN(yearlyTotal)) {
                  const derivedMonthly = divideBy12(yearlyTotal);
                  if (affectedColumn?.field === SERVICE_CHARGE_FIELD.ServiceChargePerMonth) {
                    updateValue(derivedMonthly, round, affectedFormCtrl);
                  }
                  updateValue(divideValues(derivedMonthly, spaceSize), round, ctrlTwo);
                }
                break;
              }

              case SERVICE_CHARGE_FIELD.ServiceCharge:
                if (
                  affectedColumn?.field === SERVICE_CHARGE_FIELD.ServiceChargePerMonth &&
                  ctrlTwo.value &&
                  ctrlTwo.value !== 0
                ) {
                  serviceChargeMonthly = multiplyValues(ctrlOne.value, ctrlTwo.value);
                  updateValue(serviceChargeMonthly, round, affectedFormCtrl);
                }
                if (affectedColumnTwo?.field === SERVICE_CHARGE_FIELD.ServiceChargePerYear) {
                  serviceChargeYearly = multiplyBy12(serviceChargeMonthly);
                  updateValue(serviceChargeYearly, round, affectedFormCtrlTwo);
                }
                break;

              case SERVICE_CHARGE_FIELD.ServiceChargePerMonth:
                if (affectedColumn?.field === SERVICE_CHARGE_FIELD.ServiceCharge && ctrlTwo.value) {
                  serviceChargeVal = divideValues(ctrlOne.value, ctrlTwo.value);
                  updateValue(serviceChargeVal, round, affectedFormCtrl);
                }
                if (affectedColumnTwo?.field === SERVICE_CHARGE_FIELD.ServiceChargePerYear) {
                  serviceChargeYearly = multiplyBy12(ctrlOne.value);
                  updateValue(serviceChargeYearly, round, affectedFormCtrlTwo);
                }
                break;

              case SERVICE_CHARGE_FIELD.ServiceChargePerYear:
                if (affectedColumnTwo?.field === SERVICE_CHARGE_FIELD.ServiceChargePerMonth) {
                  serviceChargeMonthly = divideBy12(ctrlOne.value);
                  updateValue(serviceChargeMonthly, round, affectedFormCtrlTwo);
                }
                if (
                  affectedColumn?.field === SERVICE_CHARGE_FIELD.ServiceCharge &&
                  ctrlTwo.value &&
                  ctrlTwo.value !== 0
                ) {
                  serviceChargeVal = divideValues(serviceChargeMonthly, ctrlTwo.value);
                  updateValue(serviceChargeVal, round, affectedFormCtrl);
                }
                break;

              default:
                break;
            }
          }
        })
      );
    }
    return undefined;
  };
}

/** The three availability asking-rent listener builders, bound to the host `round` + `locale`. */
export interface AvailabilityRatesBinders {
  addListenerToTotalRent: SingleRatesControlsListener;
  addListenerToRentYearlyPacific: MultipleRatesControlsListener;
  addListenerToAskingRentPsqm: (
    askingRentYearlyTotalCtrl: AbstractControl,
    spaceSizeCtrl: AbstractControl,
    askingRentCtrl: AbstractControl,
    shouldSkip?: boolean
  ) => Observable<unknown> | undefined;
  addListenerToServiceCharge: SingleRatesControlsListener;
}

/**
 * Build the availability asking-rent matrix / Pacific / NZ listener builders, injecting the host
 * {@link RoundFn}, the attach-time {@link LocaleContext}, and optional
 * {@link AvailabilityRatesBinderOptions}. Replaces the host
 * `availability-auto-calc-listener-helper.ts` module-level exports.
 */
export function createAvailabilityRatesBinders(
  round: RoundFn,
  locale: LocaleContext,
  options: AvailabilityRatesBinderOptions = {}
): AvailabilityRatesBinders {
  const enableChainedSizeFallbacks = options.enableChainedSizeFallbacks ?? false;
  return {
    addListenerToTotalRent: singleListenerToRatesFactory(round, locale, enableChainedSizeFallbacks),
    addListenerToRentYearlyPacific: multipleListenerToRatesFactory(calculateThreeValues, round, locale),
    addListenerToAskingRentPsqm: (askingRentYearlyTotalCtrl, spaceSizeCtrl, askingRentCtrl, shouldSkip = false) =>
      askingRentPsqmListener(askingRentYearlyTotalCtrl, spaceSizeCtrl, askingRentCtrl, round, shouldSkip),
    addListenerToServiceCharge: serviceChargeListener(round, locale, enableChainedSizeFallbacks),
  };
}
